import { type ColumnDef } from "@tanstack/react-table";
import {
  DownloadIcon,
  ImageIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PageBreadcrumbs,
  PageContainer,
  PageHeader,
} from "@/components/layout";
import { cn } from "@/lib/utils";
import BFHierarchyTable from "../../../components/hierarchy-table";
import EvaluationFormCard from "../../../components/inspections/see-inspection/evaluation-form-card";
import InfoCard from "../../../components/inspections/see-inspection/info-inspection-card";
import InfoPopover from "../../../components/info-popover";
import { MediaViewer, photosToMedia, type Media } from "@/components/media-gallery";
import { Features } from "../../../entities/features";
import {
  useInspectionItemsQuery,
  useInspectionQuery,
  useIsFeatureEnabled,
} from "../../../services/api";

import {
  clearInspectionReport,
  generateInspectionPdf,
  startInspectionPdfGeneration,
} from "./actions";
import generateColumns from "./columns";
import { selectInspectionReport } from "./selectors";

interface FlowType {
  id?: number;
  name?: string;
}

interface Inspection {
  id: string | number;
  resource?: { name?: string };
  flow?: { name?: string; flowType?: FlowType };
  conformity?: { id?: number };
  reportUrl?: string;
  evaluationForm?: unknown;
  [key: string]: unknown;
}

interface SequentialItemRow {
  conformity?: { id?: number };
  description?: string;
  /** Inspector-assessed severity on non-conformities. Null on conform / N/A. */
  severity?: Severity | null;
  /** Legacy URL list — still populated for backward compat with the v1 app. */
  photos?: string[];
  /** Rich attachment list (kind + mime + originalName). Preferred when present. */
  attachments?: Media[];
  Item?: {
    title?: string;
    photos?: string[];
  };
  [key: string]: unknown;
}

const ITEMS_PAGE_SIZE = 15;

// Conformity ids come straight from the backend `Conformity` entity:
//   0 = NOT_OK, 1 = OK, 2 = SCHEDULED, 3 = NOT_APPLICABLE
function conformityClass(id: number | undefined) {
  if (id === 1) return "text-emerald-600 dark:text-emerald-400";
  if (id === 2) return "text-sky-600 dark:text-sky-400";
  if (id === 3) return "text-muted-foreground";
  return "text-destructive";
}

function conformityLabel(
  id: number | undefined,
  t: (k: string, d?: string) => string
): string {
  if (id === 1) return t("inspections.items.yes");
  if (id === 2) return t("inspections.items.scheduled");
  if (id === 3) return t("inspections.items.na", "N/A");
  return t("inspections.items.no");
}

type Severity = 'low' | 'medium' | 'high';

function severityClass(severity: Severity): string {
  if (severity === 'high') {
    return 'bg-destructive/10 text-destructive border-destructive/30';
  }
  if (severity === 'medium') {
    return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
  }
  return 'bg-muted text-muted-foreground border-border';
}

function severityLabel(severity: Severity, t: (k: string, d?: string) => string): string {
  if (severity === 'high') return t('inspections.items.severity-high', 'High');
  if (severity === 'medium') return t('inspections.items.severity-medium', 'Medium');
  return t('inspections.items.severity-low', 'Low');
}

export default function SeeInspection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { data: inspectionFromRedux } = useInspectionQuery(id ?? "", {
    skip: !id,
  }) as { data?: Inspection };
  const [page, setPage] = useState(0);
  const { data: inspectionItems } = useInspectionItemsQuery(
    inspectionFromRedux
      ? { controlId: inspectionFromRedux.id, range: { page: page + 1 } }
      : { controlId: "" },
    { skip: !inspectionFromRedux }
  ) as { data?: unknown[] | { error?: unknown } | null };
  const inspectionReport = useSelector(selectInspectionReport()) as
    | string
    | null;
  const isResourceEvaluationFormFeatureEnabled = useIsFeatureEnabled(
    Features.FT_RESOURCE_EVALUATION_FORM
  );

  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [media, setMedia] = useState<Media[]>([]);
  const [openMediaViewer, setOpenMediaViewer] = useState(false);
  const [items, setItems] = useState<unknown[]>([]);

  // Reset local state when the URL id changes so we don't flash the
  // previous inspection's data while RTK Query refetches.
  useEffect(() => {
    setInspection(null);
    setItems([]);
    setPage(0);
    // Drop any stale report payload left over from a previous detail page
    // — defence in depth on top of the consume-and-clear in the popup
    // effect, in case Redux survives a hot reload with the report present.
    dispatch(clearInspectionReport());
  }, [id, dispatch]);

  // Only mirror RTK Query → local once the response matches the URL id,
  // otherwise leftover data from a previous detail page would briefly
  // render under the new breadcrumbs.
  useEffect(() => {
    if (inspectionFromRedux && String(inspectionFromRedux.id) === String(id)) {
      setInspection(inspectionFromRedux);
    }
  }, [inspectionFromRedux, id]);

  // Reset items as soon as we switch inspections so the table doesn't
  // render the previous inspection's rows under the new header.
  useEffect(() => {
    setItems([]);
  }, [inspection?.id]);

  useEffect(() => {
    if (!inspectionItems) return;
    if (
      !Array.isArray(inspectionItems) &&
      typeof inspectionItems === "object" &&
      "error" in inspectionItems &&
      (inspectionItems as { error?: unknown }).error
    ) {
      setItems([]);
      return;
    }
    if (Array.isArray(inspectionItems)) setItems(inspectionItems);
  }, [inspectionItems]);

  useEffect(() => {
    if (!inspectionReport) return;
    const win = window.open();
    if (win) win.document.write(inspectionReport);
    // Consume-and-clear so that navigating back to this page (or to
    // another inspection's detail) doesn't see the stale Redux payload
    // and re-open the popup.
    dispatch(clearInspectionReport());
  }, [inspectionReport, dispatch]);

  // Sequential / hierarchical tables pass `string[]` from the legacy `photos`
  // column; the new attachments column passes the richer `Media[]` directly.
  // Normalise both to the Media shape before opening the viewer.
  function openMedia(value: string[] | Media[]) {
    if (openMediaViewer) return;
    const normalised: Media[] =
      typeof value[0] === 'string' || value.length === 0
        ? photosToMedia(value as string[])
        : (value as Media[]);
    setMedia(normalised);
    setOpenMediaViewer(true);
  }

  function onSee(value: string[]) {
    openMedia(value);
  }

  function onImageClick(images: string[]) {
    openMedia(images);
  }

  function onDownloadReport() {
    if (!inspection) return;
    if (inspection.reportUrl) {
      window.open(inspection.reportUrl, "_blank");
    } else {
      dispatch(generateInspectionPdf(inspection.id));
      dispatch(startInspectionPdfGeneration(inspection.id));
    }
  }

  // ---- Sequential-flow DataTable columns ----------------------------------
  // The free-flow case still feeds the BFHierarchyTable via the legacy
  // generateColumns helper; sequential gets a native TanStack column set.
  const sequentialColumns = useMemo<ColumnDef<SequentialItemRow>[]>(
    () => [
      {
        id: "photo",
        header: " ",
        enableSorting: false,
        cell: ({ row }) => {
          const photos = row.original.Item?.photos ?? [];
          return photos.length > 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onImageClick(photos);
              }}
              className="overflow-hidden rounded-md"
              aria-label={t(
                "inspections.see-inspection.image-open",
                "Open image"
              )}
            >
              <img
                src={photos[0]}
                alt=""
                className="size-12 cursor-pointer object-cover"
              />
            </button>
          ) : (
            <ImageIcon className="size-7 text-muted-foreground" />
          );
        },
      },
      {
        id: "title",
        header: t("items.items.identifier"),
        cell: ({ row }) => row.original.Item?.title ?? "",
      },
      {
        id: "conformity",
        header: t("inspections.info-card.complaint"),
        cell: ({ row }) => {
          const conformityId = row.original.conformity?.id;
          if (conformityId == null) return null;
          // Severity arrives flat on sequential rows (`row.severity`); it can
          // also live under `report.severity` if the response is shaped like
          // the hierarchical tree leaves. Tolerate both so the chip renders
          // regardless of which use-case branch produced this payload.
          const severity =
            row.original.severity ??
            (row.original as { report?: { severity?: Severity | null } })?.report?.severity ??
            null;
          return (
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn(conformityClass(conformityId))}>
                {conformityLabel(conformityId, t)}
              </span>
              {conformityId === 0 && severity ? (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                    severityClass(severity),
                  )}
                >
                  {severityLabel(severity, t)}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "description",
        header: t("items.items.description"),
        cell: ({ row }) => (
          <div className="max-h-[180px] overflow-y-auto whitespace-pre-wrap">
            {row.original.description ?? ""}
          </div>
        ),
      },
      {
        id: "photos",
        header: t("inspections.items.attachments", { number: "" }),
        enableSorting: false,
        cell: ({ row }) => {
          // Prefer the rich attachments shape when the backend supplied it
          // (v2 client). Fall back to the legacy photos string array for
          // older submissions — the viewer normalises both into Media[].
          const attachments = row.original.attachments;
          const legacyPhotos = row.original.photos ?? [];
          const count = attachments?.length ?? legacyPhotos.length;
          if (count === 0) {
            return (
              <span className="text-muted-foreground">
                {t("inspections.items.non-attachment")}
              </span>
            );
          }
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openMedia(attachments ?? legacyPhotos);
              }}
              className="text-primary hover:underline"
            >
              {count === 1
                ? t("inspections.items.attachment", { number: count })
                : t("inspections.items.attachments", { number: count })}
            </button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  // Free-flow columns continue to come from the legacy helper because the
  // BFHierarchyTable still consumes that shape.
  const hierarchicalColumns = generateColumns({
    t,
    onSee,
    controlFlowType: inspection?.flow?.flowType as
      | { name: "sequential" }
      | undefined,
    onImageClick,
  });

  const canDownload =
    inspection != null && inspection.conformity?.id !== 2;
  const isSequentialFlow = inspection?.flow?.flowType?.id === 1;

  // Client-side paginate sequential items into chunks the size of the
  // legacy ITEMS_PAGE_SIZE so the new DataTable footer still paginates.
  const pageCount = Math.max(
    1,
    Math.ceil((items?.length ?? 0) / ITEMS_PAGE_SIZE)
  );
  const pagedItems = useMemo(
    () =>
      (items as SequentialItemRow[]).slice(
        page * ITEMS_PAGE_SIZE,
        (page + 1) * ITEMS_PAGE_SIZE
      ),
    [items, page]
  );

  return (
    <>
      <MediaViewer
        open={openMediaViewer}
        onOpenChange={(v) => setOpenMediaViewer(v)}
        items={media}
        initialIndex={0}
      />
      <PageContainer
        title={t("inspections.see-inspection.tab-title", {
          name: inspection?.resource?.name,
        })}
      >
        <PageBreadcrumbs
          items={[
            {
              label: t("sidebar.inspections", "Inspections"),
              to: "/dashboard/inspections",
            },
            { label: inspection?.resource?.name ?? "—" },
          ]}
        />
        <PageHeader
          className="mt-4"
          title={t("inspections.see-inspection.page-title", {
            name: inspection?.resource?.name,
          })}
          description={t("inspections.see-inspection.page-sub-title", {
            name: inspection?.flow?.name,
          })}
          actions={
            canDownload ? (
              <Button onClick={onDownloadReport}>
                <DownloadIcon className="size-4" />
                {t("inspections.see-inspection.download")}
              </Button>
            ) : null
          }
        />

        {inspection ? (
          <section className="mt-8 space-y-3">
            <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {t("inspections.see-inspection.inspection-id")}
            </h2>
            <InfoCard t={t} data={inspection} />
          </section>
        ) : (
          <section className="mt-8 space-y-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </section>
        )}

        {isResourceEvaluationFormFeatureEnabled &&
        inspection?.evaluationForm ? (
          <section className="mt-8 space-y-3">
            <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {t("inspections.see-inspection.inspection-evaluation")}
            </h2>
            <EvaluationFormCard
              t={t}
              data={inspection.evaluationForm as never}
              onSee={onSee}
            />
          </section>
        ) : null}

        <section className="mt-8 space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {t("inspections.see-inspection.inspection-items")}
            </h2>
            <InfoPopover
              message={t(
                "inspections.see-inspection.inspection-items-info-message"
              )}
            />
          </div>
          {/* Sequential flow → shadcn DataTable. Free flow keeps the legacy
              MUI BFHierarchyTable because shadcn has no tree-table yet. */}
          {!inspection ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          ) : isSequentialFlow ? (
            <DataTable
              columns={sequentialColumns}
              data={pagedItems}
              emptyMessage={t("common.no-data", "No data")}
              manualPagination={{
                pageIndex: page,
                pageCount,
                onPageChange: setPage,
              }}
            />
          ) : (
            <BFHierarchyTable
              id="items"
              columns={hierarchicalColumns}
              data={
                {
                  items,
                  itemCount: items.length,
                  range: { limit: ITEMS_PAGE_SIZE },
                } as unknown as unknown[]
              }
              leaf="children"
              page={page}
              onChangePage={(next: number) => setPage(next)}
              // Read-only view; no per-row expansion control here.
              // The component defaults `open=true` when this prop is
              // undefined, which then trips on items that have no
              // `.children` field.
              expandableRows={false}
            />
          )}
        </section>
      </PageContainer>
    </>
  );
}
